import { useMemo, useState } from 'react'
import { SensitiveOverlay } from '@/components/SensitiveImage'
import { sensitiveImageClass, useSensitiveMask } from '@/hooks'
import { ImagePlaceholder } from '@/components/ui'
import type { GalleryImage } from '@/types/ui'
import { cn } from '@/utils/cn'

/**
 * 图集（设计稿 D2 主图 + 下方 3 张缩略图 + 「+12」；M4 的 `1 / 16` 角标）。
 *
 * `images` 来自 `toGalleryImages(detail)`（`api/adapters.ts`），对应后端
 * `PublicPostDetail.gallery`：封面在前，其后是正文图片按文档顺序，整体按
 * 资产去重。`images` 为空时渲染设计稿定义的占位版式（蓝灰块）。
 *
 * ## 看大图交给浏览器，不自己做弹层
 *
 * 主图是一个真正的 `<a target="_blank">`，点击直接在新标签页打开原图。
 * 浏览器自带的图片视图已经有缩放、平移、另存、复制地址，以及中键/⌘-点击
 * 后台打开这些行为——自己实现一个 Modal 只会得到它的功能子集，还要额外
 * 维护焦点管理和移动端手势。
 *
 * 于是「+N」不再是「打开大图浏览器」——原先第 4 张之后的图片只能在弹层里
 * 够到，现在改成就地把剩下的缩略图展开，选中谁谁就成为主图，再点主图去新标签页。
 *
 * ## 宁可留边，不裁画面
 *
 * 主图与缩略图都用 `object-contain`：外层的 16:9 / 16:10 是为了**锁住版式高度**
 * （不让图片加载前后跳变），不代表原图就是这个比例。竖图、超宽截图套 `object-cover`
 * 会被裁掉两头——而 mod 截图恰恰经常是竖幅界面图或全景图，裁掉的往往正是要看的
 * 那部分。留边只是不好看，裁切是丢信息，所以选前者。
 *
 * 边的颜色沿用 `bg-placeholder`（与后台既有预览一致）：它跟随
 * 主题深浅，浅色页面上不会突然出现一块死黑。
 */

export interface GalleryProps {
  images: GalleryImage[]
  /** 无图时占位图的无障碍说明，通常是帖子标题。 */
  title: string
  className?: string
}

/** 设计稿在第 4 格显示「+N」，所以缩略图最多展示 3 张。 */
const VISIBLE_THUMBNAILS = 3

export function Gallery({ images, title, className }: GalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [expanded, setExpanded] = useState(false)

  const active = images[activeIndex]
  const activeMasked = useSensitiveMask(active?.url, active?.sensitive)
  const overflowCount = Math.max(0, images.length - VISIBLE_THUMBNAILS)
  const thumbnails = useMemo(
    () => (expanded ? images : images.slice(0, VISIBLE_THUMBNAILS)),
    [images, expanded],
  )

  return (
    <div className={cn('space-y-2', className)}>
      {/* 主图。16:9 固定比例，避免图片加载前后高度跳变。 */}
      <div className="relative aspect-[16/9] overflow-hidden rounded-card bg-placeholder">
        {active ? (
          <>
            {/*
              遮罩期间这个链接照旧可点：新标签页里是**原图**，浏览器不会把
              这里的滤镜带过去。遮罩管的是「不要让人在列表里猝不及防地看到」，
              主动去开一张自己点开的图不在此列。
            */}
            <a
              href={active.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block size-full"
              aria-label={`在新标签页打开第 ${activeIndex + 1} 张图片`}
            >
              <img
                src={active.url}
                alt={active.alt}
                className={cn('size-full object-contain', activeMasked && sensitiveImageClass)}
                decoding="async"
              />
            </a>
            {activeMasked && <SensitiveOverlay url={active.url} />}
          </>
        ) : (
          <>
            <ImagePlaceholder className="size-full" iconClassName="size-16" />
            <span className="sr-only">{title} 暂无预览图</span>
          </>
        )}

        {/* 移动端的「1 / 16」角标（设计稿 M4） */}
        {images.length > 1 && (
          <span className="absolute bottom-2 right-2 rounded-md bg-navy/80 px-2 py-0.5 text-xs font-medium text-navy-foreground">
            {activeIndex + 1} / {images.length}
          </span>
        )}
      </div>

      {/* 缩略图行。无图时也渲染 4 个占位格，与设计稿一致。 */}
      <ul className="grid grid-cols-4 gap-2">
        {images.length === 0
          ? Array.from({ length: 4 }, (_, index) => (
              <li key={index} className="aspect-[16/10] overflow-hidden rounded-lg">
                <ImagePlaceholder className="size-full" iconClassName="size-6 opacity-60" />
              </li>
            ))
          : thumbnails.map((image, index) => (
              <li key={image.url} className="relative">
                <GalleryThumbnail
                  image={image}
                  index={index}
                  active={index === activeIndex}
                  onSelect={() => setActiveIndex(index)}
                />
              </li>
            ))}

        {/* 展开后第 4 格就是普通缩略图了，不再需要这个入口。 */}
        {!expanded && overflowCount > 0 && (
          <li>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              aria-label={`展开其余 ${overflowCount} 张图片`}
              className="flex aspect-[16/10] w-full items-center justify-center rounded-lg bg-surface-muted text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              +{overflowCount}
            </button>
          </li>
        )}
      </ul>
    </div>
  )
}

/**
 * 一格缩略图。
 *
 * 单独拆出来只有一个理由：遮罩要按**这一张**的状态算，而 hook 不能写在
 * `map` 里。确认是按 URL 记的，所以在缩略图上确认一次，主图那张同样解开。
 */
function GalleryThumbnail({
  image,
  index,
  active,
  onSelect,
}: {
  image: GalleryImage
  index: number
  active: boolean
  onSelect: () => void
}) {
  const masked = useSensitiveMask(image.url, image.sensitive)

  return (
    <>
      <button
        type="button"
        onClick={onSelect}
        aria-label={`查看第 ${index + 1} 张图片`}
        aria-current={active}
        className={cn(
          'aspect-[16/10] w-full overflow-hidden rounded-lg border-2 bg-placeholder transition-colors',
          active ? 'border-primary' : 'border-transparent hover:border-border-strong',
        )}
      >
        <img
          src={image.url}
          alt=""
          loading="lazy"
          decoding="async"
          className={cn('size-full object-contain', masked && sensitiveImageClass)}
        />
      </button>
      {masked && <SensitiveOverlay url={image.url} compact className="rounded-lg" />}
    </>
  )
}
